/**
 * DemoDisabled Component
 *
 * Wrapper component that adds a "Unavailable in Demo" tooltip to disabled elements.
 * Used to indicate features that are disabled in the demo version of the application.
 *
 * Note: Disabled buttons don't trigger hover events directly, so we wrap them in a
 * span that captures the hover and shows the tooltip.
 */

import type { ReactNode } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface DemoDisabledProps {
  /** The content to wrap (typically a Button) */
  children: ReactNode;
  /** Whether to show the demo disabled tooltip. When false, renders children directly. */
  disabled?: boolean;
  /** Custom tooltip message (defaults to "Unavailable in Demo") */
  message?: string;
}

/**
 * Wraps a component (typically a button) with a tooltip explaining that
 * the feature is unavailable in the demo version.
 *
 * @example
 * <DemoDisabled disabled={readOnly}>
 *   <Button disabled={readOnly} onClick={handleClick}>
 *     Add Client
 *   </Button>
 * </DemoDisabled>
 */
export function DemoDisabled({
  children,
  disabled = false,
  message = 'Unavailable in Demo',
}: DemoDisabledProps) {
  if (!disabled) {
    return <>{children}</>;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {/*
         * Wrap in span with pointer-events-auto to capture hover events.
         * Disabled buttons have pointer-events-none, so the wrapper must
         * explicitly enable pointer events to show the tooltip.
         */}
        <span className="inline-block cursor-not-allowed">
          <span className="pointer-events-none">{children}</span>
        </span>
      </TooltipTrigger>
      <TooltipContent>{message}</TooltipContent>
    </Tooltip>
  );
}
